import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';
import { ko as koLocale } from 'date-fns/locale';
import { supabase } from '@/lib/supabase';
import { formatKRW, formatTimeKo, toKst } from '@/lib/format';
import { formatKoreanPhoneInput, stripPhone } from '@/lib/phone';
import type { AvailableSlot, PaymentMethod } from '@mudong/shared';

interface Step1Selection {
  starts_at: string;
  duration_minutes: number;
  price_krw: number;
}

const SHOW_DAYS = 30;

export function BookingFlowPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [selected, setSelected] = useState<Step1Selection | null>(null);
  const [name, setName] = useState('');
  const [birthY, setBirthY] = useState('');
  const [birthM, setBirthM] = useState('');
  const [birthD, setBirthD] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | ''>('');
  const [consent, setConsent] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('toss_pay');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const today = useMemo(() => new Date(), []);
  const dates = useMemo(
    () => Array.from({ length: SHOW_DAYS }, (_, i) => addDays(today, i)),
    [today],
  );
  const [activeDay, setActiveDay] = useState<string>(format(today, 'yyyy-MM-dd'));

  const slotsQuery = useQuery({
    queryKey: ['available-slots', slug, format(today, 'yyyy-MM-dd')],
    enabled: !!slug,
    queryFn: async (): Promise<AvailableSlot[]> => {
      const { data, error } = await supabase.rpc('get_available_slots', {
        p_slug: slug!,
        p_from_date: format(today, 'yyyy-MM-dd'),
        p_to_date: format(addDays(today, SHOW_DAYS), 'yyyy-MM-dd'),
      });
      if (error) throw error;
      return (data ?? []) as AvailableSlot[];
    },
  });

  const slotsByDay = useMemo(() => {
    const map: Record<string, AvailableSlot[]> = {};
    for (const s of slotsQuery.data ?? []) {
      const day = format(toKst(s.starts_at), 'yyyy-MM-dd');
      if (!map[day]) map[day] = [];
      map[day].push(s);
    }
    return map;
  }, [slotsQuery.data]);

  async function submit() {
    setErr(null);
    setSubmitting(true);
    try {
      const birth_date = `${birthY.padStart(4, '0')}-${birthM.padStart(2, '0')}-${birthD.padStart(2, '0')}`;
      const { data, error } = await supabase.functions.invoke('create-booking', {
        body: {
          shaman_slug: slug,
          starts_at: selected!.starts_at,
          payment_method: paymentMethod,
          customer: {
            name: name.trim(),
            birth_date,
            phone: stripPhone(phone),
            gender,
            privacy_consent: consent,
          },
        },
      });
      if (error) throw new Error(error.message ?? '예약에 실패했습니다');
      const res = data as {
        booking_id?: string;
        cancel_token?: string;
        error?: string;
      };
      if (res.error) throw new Error(res.error);
      navigate(`/booking/${res.booking_id}?token=${res.cancel_token}`, { replace: true });
    } catch (e) {
      setErr((e as Error).message);
      setSubmitting(false);
    }
  }

  if (!slug) return null;

  return (
    <div className="space-y-5">
      <StepHeader step={step} />
      {step === 1 && (
        <Step1
          dates={dates}
          activeDay={activeDay}
          onPickDay={setActiveDay}
          slots={slotsByDay[activeDay] ?? []}
          loading={slotsQuery.isLoading}
          onSelect={(s) => {
            setSelected(s);
            setStep(2);
          }}
        />
      )}
      {step === 2 && selected && (
        <Step2
          selected={selected}
          name={name} setName={setName}
          birthY={birthY} setBirthY={setBirthY}
          birthM={birthM} setBirthM={setBirthM}
          birthD={birthD} setBirthD={setBirthD}
          phone={phone} setPhone={setPhone}
          gender={gender} setGender={setGender}
          consent={consent} setConsent={setConsent}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
        />
      )}
      {step === 3 && selected && (
        <Step3
          selected={selected}
          paymentMethod={paymentMethod}
          onChange={setPaymentMethod}
          onBack={() => setStep(2)}
          onNext={() => setStep(4)}
        />
      )}
      {step === 4 && selected && (
        <Step4
          selected={selected}
          paymentMethod={paymentMethod}
          name={name}
          phone={phone}
          err={err}
          submitting={submitting}
          onBack={() => setStep(3)}
          onSubmit={submit}
        />
      )}
    </div>
  );
}

function StepHeader({ step }: { step: 1 | 2 | 3 | 4 }) {
  const labels = ['시간', '정보', '결제', '확인'];
  return (
    <div className="flex justify-between gap-2 text-base">
      {labels.map((l, i) => {
        const n = (i + 1) as 1 | 2 | 3 | 4;
        return (
          <div
            key={l}
            className={`flex-1 text-center py-2 rounded-lg ${
              n === step ? 'bg-brand text-white font-semibold' : 'bg-white border border-ink-line text-ink-muted'
            }`}
          >
            {n}. {l}
          </div>
        );
      })}
    </div>
  );
}

function Step1(props: {
  dates: Date[];
  activeDay: string;
  onPickDay: (d: string) => void;
  slots: AvailableSlot[];
  loading: boolean;
  onSelect: (s: Step1Selection) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="text-xl font-bold">날짜와 시간을 선택해주세요</div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
        {props.dates.map((d) => {
          const key = format(d, 'yyyy-MM-dd');
          const active = key === props.activeDay;
          return (
            <button
              key={key}
              onClick={() => props.onPickDay(key)}
              className={`min-w-[64px] rounded-lg py-2 text-base text-center ${
                active ? 'bg-brand text-white' : 'bg-white border border-ink-line'
              }`}
            >
              <div className="text-sm">{format(d, 'EEE', { locale: koLocale })}</div>
              <div className="font-bold">{format(d, 'd')}</div>
            </button>
          );
        })}
      </div>

      {props.loading && <div className="text-ink-muted text-center py-8">불러오는 중…</div>}
      {!props.loading && !props.slots.length && (
        <div className="card text-center text-ink-muted">이 날은 예약 가능한 시간이 없어요.</div>
      )}
      <ul className="space-y-2">
        {props.slots.map((s) => (
          <li key={s.starts_at}>
            <button
              onClick={() => props.onSelect(s)}
              className="w-full bg-white border border-ink-line rounded-lg min-h-touch px-4 flex items-center justify-between text-lg active:scale-[0.99]"
            >
              <span className="font-semibold">{formatTimeKo(s.starts_at, 'a h:mm')}</span>
              <span className="text-ink-muted text-base">
                {s.duration_minutes}분 · {formatKRW(s.price_krw)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Step2(props: {
  selected: Step1Selection;
  name: string; setName: (v: string) => void;
  birthY: string; setBirthY: (v: string) => void;
  birthM: string; setBirthM: (v: string) => void;
  birthD: string; setBirthD: (v: string) => void;
  phone: string; setPhone: (v: string) => void;
  gender: '' | 'male' | 'female'; setGender: (v: 'male' | 'female') => void;
  consent: boolean; setConsent: (v: boolean) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const valid = props.name.trim() &&
    /^\d{4}$/.test(props.birthY) && /^\d{1,2}$/.test(props.birthM) && /^\d{1,2}$/.test(props.birthD) &&
    stripPhone(props.phone).length >= 10 && props.gender && props.consent;

  return (
    <div className="space-y-4">
      <div className="card space-y-1">
        <div className="text-base text-ink-muted">선택한 시간</div>
        <div className="font-bold text-lg">{formatTimeKo(props.selected.starts_at, 'M월 d일(EEE) a h:mm')}</div>
      </div>

      <div className="card space-y-4">
        <div>
          <label className="label">이름</label>
          <input className="field" value={props.name} onChange={(e) => props.setName(e.target.value)} />
        </div>

        <div>
          <label className="label">생년월일</label>
          <div className="grid grid-cols-3 gap-2">
            <input className="field" inputMode="numeric" placeholder="년 (1990)" value={props.birthY} onChange={(e) => props.setBirthY(e.target.value.replace(/\D/g, '').slice(0, 4))} />
            <input className="field" inputMode="numeric" placeholder="월" value={props.birthM} onChange={(e) => props.setBirthM(e.target.value.replace(/\D/g, '').slice(0, 2))} />
            <input className="field" inputMode="numeric" placeholder="일" value={props.birthD} onChange={(e) => props.setBirthD(e.target.value.replace(/\D/g, '').slice(0, 2))} />
          </div>
        </div>

        <div>
          <label className="label">휴대전화번호</label>
          <input
            className="field"
            inputMode="tel"
            value={props.phone}
            onChange={(e) => props.setPhone(formatKoreanPhoneInput(e.target.value))}
          />
        </div>

        <div>
          <label className="label">성별</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className={`btn ${props.gender === 'male' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => props.setGender('male')}
            >남자</button>
            <button
              type="button"
              className={`btn ${props.gender === 'female' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => props.setGender('female')}
            >여자</button>
          </div>
        </div>

        <label className="flex items-start gap-2 text-base">
          <input
            type="checkbox"
            className="mt-1 size-5"
            checked={props.consent}
            onChange={(e) => props.setConsent(e.target.checked)}
          />
          <span>
            <a href="/privacy" className="underline" target="_blank">개인정보 수집·이용</a>에 동의합니다 (필수)
          </span>
        </label>
      </div>

      <div className="flex gap-2">
        <button className="btn-secondary flex-1" onClick={props.onBack}>이전</button>
        <button className="btn-primary flex-[2]" disabled={!valid} onClick={props.onNext}>다음</button>
      </div>
    </div>
  );
}

function Step3(props: {
  selected: Step1Selection;
  paymentMethod: PaymentMethod;
  onChange: (m: PaymentMethod) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="card space-y-1">
        <div className="text-base text-ink-muted">결제 금액</div>
        <div className="font-bold text-2xl">{formatKRW(props.selected.price_krw)}</div>
      </div>

      <div className="card space-y-2">
        <button
          className={`w-full text-left rounded-lg border p-4 ${
            props.paymentMethod === 'toss_pay' ? 'border-brand bg-brand-50' : 'border-ink-line bg-white'
          }`}
          onClick={() => props.onChange('toss_pay')}
        >
          <div className="font-bold text-lg">토스 / 카드로 바로 결제</div>
          <div className="text-base text-ink-muted">결제 즉시 예약이 확정됩니다.</div>
        </button>
        <button
          className={`w-full text-left rounded-lg border p-4 ${
            props.paymentMethod === 'virtual_account' ? 'border-brand bg-brand-50' : 'border-ink-line bg-white'
          }`}
          onClick={() => props.onChange('virtual_account')}
        >
          <div className="font-bold text-lg">가상계좌로 입금</div>
          <div className="text-base text-ink-muted">전용 계좌로 입금하시면 자동으로 확정됩니다 (24시간 이내).</div>
        </button>
      </div>

      <div className="flex gap-2">
        <button className="btn-secondary flex-1" onClick={props.onBack}>이전</button>
        <button className="btn-primary flex-[2]" onClick={props.onNext}>다음</button>
      </div>
    </div>
  );
}

function Step4(props: {
  selected: Step1Selection;
  paymentMethod: PaymentMethod;
  name: string;
  phone: string;
  err: string | null;
  submitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="card space-y-3">
        <h2 className="text-xl font-bold">확인 후 예약하기</h2>
        <Row label="일시" value={formatTimeKo(props.selected.starts_at, 'M월 d일(EEE) a h:mm')} />
        <Row label="소요 시간" value={`${props.selected.duration_minutes}분`} />
        <Row label="이름" value={props.name} />
        <Row label="연락처" value={props.phone} />
        <Row label="결제 방법" value={props.paymentMethod === 'toss_pay' ? '토스/카드' : '가상계좌'} />
        <Row label="금액" value={formatKRW(props.selected.price_krw)} bold />
      </div>

      {props.err && <div className="text-danger text-base text-center">{props.err}</div>}

      <div className="flex gap-2">
        <button className="btn-secondary flex-1" onClick={props.onBack} disabled={props.submitting}>이전</button>
        <button className="btn-primary flex-[2]" onClick={props.onSubmit} disabled={props.submitting}>
          {props.submitting ? '예약 중…' : '예약하기'}
        </button>
      </div>

      <p className="text-base text-ink-muted text-center">
        예약 후 입금/결제 안내가 이어집니다. (현재 단계는 예약 접수까지만이며, 실제 결제 연동은 다음 업데이트에서 추가됩니다.)
      </p>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between items-center gap-3 text-base">
      <span className="text-ink-muted">{label}</span>
      <span className={bold ? 'font-bold text-lg' : ''}>{value}</span>
    </div>
  );
}
